import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import CustomDropdown from '../ui/CustomDropdown';
import {
  Save, X, Upload, FileUp, Loader2, ChevronUp, ChevronDown, Tag,
} from 'lucide-react';
import PlatformPicker from './PlatformPicker';
import { tagsToArray, tagsToCsv } from './skillsUtils';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALLOWED_IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMG_SIZE = 10 * 1024 * 1024;

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--dp-bg-input, #252525)',
  border: '1px solid var(--dp-border-subtle)',
  borderRadius: '8px',
  color: 'var(--dp-text-primary)',
  fontFamily: 'var(--dp-font)',
  fontSize: '13px',
  outline: 'none',
  transition: 'all 0.3s',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--dp-text-secondary)',
  fontFamily: 'var(--dp-font)',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const dialogContentStyleWithDropdown = {
  background: 'var(--dp-bg-glass, rgba(28,28,28,0.85))',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  border: '1px solid var(--dp-border-medium)',
  borderRadius: '16px',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
  maxWidth: '640px',
  maxHeight: '85vh',
  overflow: 'visible',
  padding: '0',
};

const dialogInnerStyle = {
  padding: '24px',
  maxHeight: '85vh',
  overflowY: 'auto',
  overflowX: 'hidden',
};

const smallActionBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '5px',
  padding: '5px 11px', borderRadius: '8px', fontSize: '11.5px', fontWeight: 600,
  fontFamily: 'var(--dp-font)', cursor: 'pointer',
  border: '1px solid rgba(13, 174, 255, 0.3)', background: 'rgba(13, 174, 255, 0.06)',
  color: 'var(--dp-blue-500)', transition: 'all 0.2s ease', lineHeight: 1.4,
};

/**
 * CRUD de skill do USUÁRIO (tabela prompts) com paridade visual/funcional
 * com as skills do sistema: imagens rotuladas (upload user-upload no R2),
 * instruções de uso, link de tutorial. Mantém o vínculo com canal (fixo
 * após a criação, regra existente) e preserva favorito/idioma no PUT.
 * `editing` é o prompt CRU (tags CSV) ou null para criar.
 */
const UserSkillModal = ({ editing, canais = [], onClose, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    canal_id: 'global',
    nome: '',
    categoria: '',
    anotacoes: '',
    conteudo: '',
    instrucoes: '',
    video_url: '',
    tags: [],
    plataformas: [],
    imagens: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const mdInputRef = useRef(null);
  const imgInputRef = useRef(null);

  const canalOptions = [
    { value: 'global', label: t('skills.save_dialog.global') },
    ...canais.map((c) => ({ value: c.id, label: c.nome })),
  ];

  useEffect(() => {
    if (editing) {
      setForm({
        canal_id: editing.canal_id || 'global',
        nome: editing.nome || '',
        categoria: editing.categoria || '',
        anotacoes: editing.anotacoes || '',
        conteudo: editing.conteudo || '',
        instrucoes: editing.instrucoes || '',
        video_url: editing.video_url || '',
        tags: tagsToArray(editing.tags),
        plataformas: Array.isArray(editing.plataformas) ? editing.plataformas : [],
        imagens: Array.isArray(editing.imagens) ? editing.imagens : [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  const addTag = () => {
    const val = tagInput.trim();
    if (!val) return;
    if (!form.tags.includes(val)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, val] }));
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((tg) => tg !== tag) }));
  };

  const handleMdFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, conteudo: String(reader.result || '') }));
      toast.success(t('skills.admin.md_loaded'));
    };
    reader.readAsText(file);
  };

  const handleImgFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploadingImg(true);
    try {
      for (const file of files) {
        if (!ALLOWED_IMG_TYPES.includes(file.type)) {
          toast.error(`${file.name}: ${t('skills.admin.image_type_error')}`);
          continue;
        }
        if (file.size > MAX_IMG_SIZE) {
          toast.error(`${file.name}: ${t('skills.admin.image_size_error')}`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/skills/user-upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const { url } = res.data;
        setForm((prev) => ({
          ...prev,
          imagens: [...prev.imagens, { url, label: '', ordem: prev.imagens.length }],
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('skills.admin.error_upload'));
    } finally {
      setUploadingImg(false);
    }
  };

  const setImgLabel = (index, label) => {
    setForm((prev) => ({
      ...prev,
      imagens: prev.imagens.map((img, i) => (i === index ? { ...img, label } : img)),
    }));
  };

  const moveImg = (index, dir) => {
    setForm((prev) => {
      const arr = [...prev.imagens];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      return { ...prev, imagens: arr };
    });
  };

  const removeImg = (index) => {
    setForm((prev) => ({ ...prev, imagens: prev.imagens.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!form.nome.trim() || !form.conteudo.trim()) {
      toast.error(t('skills.my.error_required'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        canal_id: form.canal_id === 'global' ? null : form.canal_id,
        nome: form.nome.trim(),
        categoria: form.categoria.trim() || null,
        conteudo: form.conteudo,
        anotacoes: form.anotacoes.trim() || null,
        // Preservados no PUT (o endpoint substitui todos os campos base):
        idioma_sugerido: editing?.idioma_sugerido ?? null,
        favorito: editing?.favorito ?? false,
        tags: tagsToCsv(form.tags) || null,
        imagens: form.imagens.map((img, i) => ({ url: img.url, label: img.label || '', ordem: i })),
        instrucoes: form.instrucoes.trim(),
        video_url: form.video_url.trim(),
        plataformas: form.plataformas,
      };
      if (editing) {
        await axios.put(`${API}/prompts/${editing.id}`, payload);
        toast.success(t('skills.my.updated'));
      } else {
        await axios.post(`${API}/prompts`, payload);
        toast.success(t('skills.my.created'));
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('skills.admin.error_save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent style={dialogContentStyleWithDropdown}>
        <div style={dialogInnerStyle}>
          <DialogHeader>
            <DialogTitle style={{
              fontSize: '16px', fontWeight: 700, color: 'var(--dp-text-primary)',
              fontFamily: 'var(--dp-font)', marginBottom: '16px',
            }}>
              {editing ? t('skills.my.edit_skill') : t('skills.my.new_skill')}
            </DialogTitle>
          </DialogHeader>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Canal (fixo após criação, regra existente dos prompts) */}
            <div>
              <label style={labelStyle}>{t('skills.save_dialog.channel')}</label>
              {editing ? (
                <>
                  <input
                    type="text"
                    value={canalOptions.find((o) => String(o.value) === String(form.canal_id))?.label || t('skills.save_dialog.global')}
                    disabled
                    style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
                  />
                  <span style={{
                    display: 'block', marginTop: '4px', fontSize: '11px',
                    color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
                  }}>
                    {t('skills.my.channel_locked')}
                  </span>
                </>
              ) : (
                <CustomDropdown
                  options={canalOptions}
                  value={form.canal_id}
                  onChange={(v) => setForm((prev) => ({ ...prev, canal_id: v }))}
                />
              )}
            </div>

            {/* Nome */}
            <div>
              <label style={labelStyle}>{t('skills.admin.nome')} *</label>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                placeholder={t('skills.my.nome_placeholder')}
                style={inputStyle}
              />
            </div>

            {/* Categoria */}
            <div>
              <label style={labelStyle}>{t('skills.admin.categoria')}</label>
              <input
                type="text"
                value={form.categoria}
                onChange={(e) => setForm((prev) => ({ ...prev, categoria: e.target.value }))}
                placeholder={t('skills.admin.categoria_placeholder')}
                style={inputStyle}
              />
            </div>

            {/* Anotações (vira a descrição do card) */}
            <div>
              <label style={labelStyle}>{t('skills.my.anotacoes')}</label>
              <textarea
                value={form.anotacoes}
                onChange={(e) => setForm((prev) => ({ ...prev, anotacoes: e.target.value }))}
                placeholder={t('skills.my.anotacoes_placeholder')}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.5' }}
              />
            </div>

            {/* Plataformas compatíveis */}
            <div>
              <label style={labelStyle}>{t('skills.admin.plataformas')}</label>
              <PlatformPicker
                value={form.plataformas}
                onChange={(next) => setForm((prev) => ({ ...prev, plataformas: next }))}
              />
            </div>

            {/* Tags */}
            <div>
              <label style={labelStyle}>{t('skills.admin.tags')}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                  }}
                  placeholder={t('skills.admin.tag_placeholder')}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={addTag}
                  style={{
                    padding: '0 14px', borderRadius: '8px',
                    border: '1px solid var(--dp-border-medium)', background: 'var(--dp-bg-elevated)',
                    color: 'var(--dp-text-primary)', cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <Tag size={13} />
                </button>
              </div>
              {form.tags.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {form.tags.map((tag) => (
                    <span key={tag} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '3px 10px', borderRadius: '9999px', fontSize: '11px',
                      fontFamily: 'var(--dp-font)', color: 'var(--dp-blue-500)',
                      background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.22)',
                    }}>
                      {tag}
                      <X size={11} style={{ cursor: 'pointer' }} onClick={() => removeTag(tag)} />
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Conteúdo + upload .md client-side */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>{t('skills.admin.conteudo')} *</label>
                <button type="button" onClick={() => mdInputRef.current?.click()} style={smallActionBtnStyle}>
                  <FileUp size={12} />
                  {t('skills.admin.upload_md')}
                </button>
                <input
                  ref={mdInputRef}
                  type="file"
                  accept=".md,.markdown,.txt,text/markdown,text/plain"
                  onChange={handleMdFile}
                  style={{ display: 'none' }}
                />
              </div>
              <textarea
                value={form.conteudo}
                onChange={(e) => setForm((prev) => ({ ...prev, conteudo: e.target.value }))}
                placeholder={t('skills.admin.conteudo_placeholder')}
                rows={9}
                style={{
                  ...inputStyle, resize: 'vertical', lineHeight: '1.55',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                  fontSize: '12px',
                }}
              />
            </div>

            {/* Instruções de uso */}
            <div>
              <label style={labelStyle}>{t('skills.admin.instrucoes')}</label>
              <textarea
                value={form.instrucoes}
                onChange={(e) => setForm((prev) => ({ ...prev, instrucoes: e.target.value }))}
                placeholder={t('skills.admin.instrucoes_placeholder')}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.55' }}
              />
            </div>

            {/* Link do tutorial */}
            <div>
              <label style={labelStyle}>{t('skills.admin.video_url')}</label>
              <input
                type="url"
                value={form.video_url}
                onChange={(e) => setForm((prev) => ({ ...prev, video_url: e.target.value }))}
                placeholder={t('skills.admin.video_url_placeholder')}
                style={inputStyle}
              />
            </div>

            {/* Imagens */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>{t('skills.admin.images')}</label>
                <button
                  type="button"
                  onClick={() => imgInputRef.current?.click()}
                  disabled={uploadingImg}
                  style={{
                    ...smallActionBtnStyle,
                    cursor: uploadingImg ? 'wait' : 'pointer',
                    opacity: uploadingImg ? 0.6 : 1,
                  }}
                >
                  {uploadingImg
                    ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Upload size={12} />}
                  {uploadingImg ? t('skills.admin.uploading') : t('skills.admin.add_images')}
                </button>
                <input
                  ref={imgInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleImgFiles}
                  style={{ display: 'none' }}
                />
              </div>
              {form.imagens.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {form.imagens.map((img, i) => (
                    <div key={`${img.url}-${i}`} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px', borderRadius: '10px',
                      border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-input)',
                    }}>
                      <img
                        src={img.url}
                        alt=""
                        onError={(e) => { e.target.style.visibility = 'hidden'; }}
                        style={{
                          width: '80px', height: '45px', objectFit: 'cover',
                          borderRadius: '6px', flexShrink: 0,
                          border: '1px solid var(--dp-border-subtle)',
                        }}
                      />
                      <input
                        type="text"
                        value={img.label || ''}
                        onChange={(e) => setImgLabel(i, e.target.value)}
                        placeholder={t('skills.admin.image_label_placeholder')}
                        style={{ ...inputStyle, flex: 1, padding: '7px 11px', fontSize: '12px' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                        <button type="button" onClick={() => moveImg(i, -1)} disabled={i === 0} style={{
                          border: 'none', background: 'transparent', cursor: i === 0 ? 'default' : 'pointer',
                          color: i === 0 ? 'var(--dp-text-tertiary)' : 'var(--dp-text-secondary)', padding: 0,
                        }}>
                          <ChevronUp size={14} />
                        </button>
                        <button type="button" onClick={() => moveImg(i, 1)} disabled={i === form.imagens.length - 1} style={{
                          border: 'none', background: 'transparent',
                          cursor: i === form.imagens.length - 1 ? 'default' : 'pointer',
                          color: i === form.imagens.length - 1 ? 'var(--dp-text-tertiary)' : 'var(--dp-text-secondary)', padding: 0,
                        }}>
                          <ChevronDown size={14} />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImg(i)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: '26px', height: '26px', borderRadius: '7px', flexShrink: 0,
                          border: '1px solid var(--dp-border-subtle)', background: 'transparent',
                          color: 'var(--dp-text-secondary)', cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-red-500, #F87171)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Salvar */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '4px' }}>
              <button
                type="button"
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={handleSave}
                disabled={saving || uploadingImg}
                style={{ opacity: saving || uploadingImg ? 0.6 : 1 }}
              >
                {saving
                  ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  : <Save size={14} />}
                {saving ? t('skills.admin.saving') : t('skills.admin.save')}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserSkillModal;
